import { useEffect } from 'react';
import { create } from 'zustand';
import { isApiRequestError } from '@/api/client';
import { setErrorInterceptor, useToasts } from '@/stores/toasts';

/**
 * One mechanism for the designed error states of every game (design/system "06 Errors"):
 * games keep calling `toast.error(e)`; errors that have a designed screen are taken over here and
 * shown by <GameErrorHost/> (modal on desktop, bottom sheet on phones). Everything else stays a toast.
 *
 *   INSUFFICIENT_BALANCE   → "Not enough tokens" (need / have, Claim daily bonus, Set bet to <have>)
 *   LIMIT_TOKENS_REACHED   → daily token limit reached (→ Responsible play)
 *   LIMIT_TIME_REACHED     → daily time limit reached (→ Responsible play)
 *   ON_BREAK               → break until … (→ Responsible play)
 *   ROUND_ALREADY_STARTED  → orange notice "bets for this round are closed"
 *
 * A game may offer "Set bet to <have>" by registering its setter: `useSetBetHandler(setAmount)`.
 */
export type GameErrorView =
  | { kind: 'balance'; need: number | null; have: number }
  | { kind: 'limitTokens'; limit: number; used: number }
  | { kind: 'limitTime'; limit: number }
  | { kind: 'break'; until: string | null };

interface GameErrorState {
  current: GameErrorView | null;
  show(v: GameErrorView): void;
  clear(): void;
}

export const useGameError = create<GameErrorState>()((set) => ({
  current: null,
  show: (current) => set({ current }),
  clear: () => set({ current: null }),
}));

const num = (v: unknown, d = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && v !== '' && Number.isFinite(Number(v)) ? Number(v) : d);

/** Maps an API error to a designed view; null = not one of ours. */
export function toGameErrorView(e: unknown): GameErrorView | null {
  if (!isApiRequestError(e)) return null;
  const p = e.params;
  switch (e.code) {
    case 'INSUFFICIENT_BALANCE':
      return { kind: 'balance', need: p.need === undefined ? null : num(p.need), have: num(p.balance) };
    case 'LIMIT_TOKENS_REACHED':
      return { kind: 'limitTokens', limit: num(p.limit), used: num(p.used) };
    case 'LIMIT_TIME_REACHED':
      return { kind: 'limitTime', limit: num(p.limit) };
    case 'ON_BREAK':
      return { kind: 'break', until: typeof p.until === 'string' ? p.until : null };
    default:
      return null;
  }
}

/**
 * The back office (`data-admin` on its page) is not a game: an admin debit larger than the player's balance answers
 * INSUFFICIENT_BALANCE, and the player's "Not enough tokens · Claim daily bonus" modal would be about the admin's own wallet.
 */
function onAdminPage(): boolean {
  return typeof document !== 'undefined' && !!document.querySelector('[data-admin]');
}

/** The interceptor installed into `toast.error`. */
export function interceptGameError(e: unknown): boolean {
  if (onAdminPage()) return false;
  const view = toGameErrorView(e);
  if (view) {
    useGameError.getState().show(view);
    return true;
  }
  if (isApiRequestError(e) && e.code === 'ROUND_ALREADY_STARTED') {
    useToasts.getState().push({ kind: 'notice', titleId: 'errors.ROUND_ALREADY_STARTED', textId: 'common.errorState.roundStarted.closed' });
    return true;
  }
  return false;
}

export function installGameErrorInterceptor(): () => void {
  setErrorInterceptor(interceptGameError);
  return () => setErrorInterceptor(null);
}

/* ------------------------------------------------ "Set bet to <have>" */
let setBetHandler: ((amount: number) => void) | null = null;

export function getSetBetHandler(): ((amount: number) => void) | null {
  return setBetHandler;
}

/** A game screen registers its bet setter while mounted (optional). */
export function useSetBetHandler(fn: ((amount: number) => void) | null): void {
  useEffect(() => {
    if (!fn) return;
    setBetHandler = fn;
    return () => {
      if (setBetHandler === fn) setBetHandler = null;
    };
  }, [fn]);
}
